/**
 * Jitter-buffer-like typewriter for streamed text.
 */

import { clamp } from 'lodash-es';

/** How long to drain the pending characters */
const DRAIN_SECONDS = 0.5;
/** Min characters per second */
const MIN_CPS = 40;
/** Max characters per second */
const MAX_CPS = 500;
/** How smooth the speed changes are */
const SMOOTHING = 0.15;
/** FPS limiter */
const MAX_FRAME_SECONDS = 0.05;

/**
 * @param render - renders current text
 * @param onDone - runs once stream has ended
 */
export const createTypewriter = (render, onDone) => {
    let buffer = '';
    let revealed = 0;
    let speed = MIN_CPS;
    let closed = false;
    let frameId = null;
    let lastTs = 0;

    const step = (now) => {
        const elapsed = lastTs ? Math.min((now - lastTs) / 1000, MAX_FRAME_SECONDS) : 0;
        lastTs = now;

        const pending = buffer.length - revealed;
        const target = closed ? MAX_CPS : clamp(pending / DRAIN_SECONDS, MIN_CPS, MAX_CPS);
        speed += (target - speed) * SMOOTHING; // smooth speed changes

        // Save float value so that we don't discard sub-frame fractional characters when speed is low.
        // Only floor it for rendering.
        const prev = Math.floor(revealed);
        revealed = Math.min(revealed + speed * elapsed, buffer.length);

        // render only if we have new characters to show
        if (Math.floor(revealed) !== prev) {
            render(buffer.slice(0, Math.floor(revealed)));
        }

        if (revealed >= buffer.length) {
            // caught up, go idle until more arrives
            frameId = null;
            lastTs = 0;
            if (closed) {
                onDone();
            }
        } else {
            frameId = requestAnimationFrame(step);
        }
    };

    const resume = () => {
        // if we are already running, don't start another loop
        if (frameId === null) {
            frameId = requestAnimationFrame(step);
        }
    };

    return {
        push: (text) => {
            buffer += text;
            resume();
        },

        close: () => {
            closed = true;
            resume();
        },

        cancel: () => {
            if (frameId !== null) {
                cancelAnimationFrame(frameId);
                frameId = null;
            }
        },
    };
};
